// The org Usage page's tabs, validated from `?tab=` so refresh and links keep
// the tab. Also carries the hosted-checkout return params.

export const USAGE_TABS = ["usage", "limits", "invoices", "balance"] as const;
export type UsageTab = (typeof USAGE_TABS)[number];

type CheckoutOutcome = "success" | "cancelled";
export type CheckoutReturn = { checkout?: CheckoutOutcome; setup?: CheckoutOutcome };

export type UsageSearch = CheckoutReturn & { tab?: UsageTab };

const isOutcome = (v: unknown): v is CheckoutOutcome => v === "success" || v === "cancelled";

/** Hosted checkout's return params, on the Usage page and on the old billing URL that redirects to it. */
export function validateCheckoutReturn(search: Record<string, unknown>): CheckoutReturn {
  return {
    ...(isOutcome(search.checkout) ? { checkout: search.checkout } : {}),
    ...(isOutcome(search.setup) ? { setup: search.setup } : {}),
  };
}

export function validateUsageSearch(search: Record<string, unknown>): UsageSearch {
  return {
    ...(USAGE_TABS.includes(search.tab as UsageTab) ? { tab: search.tab as UsageTab } : {}),
    ...validateCheckoutReturn(search),
  };
}

type UsagePerms = { canViewUsage?: boolean | null; canViewBilling?: boolean | null; canViewSpendLimits?: boolean | null };

/** Which tabs the viewer gets. Unknown permissions (undefined) allow every tab, like the sidebar. */
export function allowedUsageTabs(perms: UsagePerms | null | undefined): Record<UsageTab, boolean> {
  const canBilling = perms?.canViewBilling !== false;
  return {
    usage: perms?.canViewUsage !== false,
    limits: canBilling || perms?.canViewSpendLimits === true,
    invoices: canBilling,
    balance: canBilling,
  };
}

/** Spending limits is the viewer's only tab (an org member), so the page and its nav entry are named for it. */
export function limitsOnly(perms: UsagePerms | null | undefined): boolean {
  const allowed = allowedUsageTabs(perms);
  return USAGE_TABS.filter((t) => allowed[t]).join() === "limits";
}
